// FILE: src/blueprint/app/BlueprintApp.jsx
// GOAL: Top-level Blueprint Runner React root that wires global providers and the router.

import { useEffect, useMemo, useState } from 'react';
import { getPilotConsent } from '../api/getPilotConsent.js';
import { postPilotConsent } from '../api/postPilotConsent.js';
import { postSecurityEvent } from '../api/postSecurityEvent.js';
import PilotConsentModal from '../components/PilotConsentModal.jsx';
import BlueprintProvider from '../state/BlueprintProvider.jsx';
import PacksProvider from '../state/PacksProvider.jsx';
import BlueprintRouter from './BlueprintRouter.jsx';

function asString(value) {
  return value == null ? '' : String(value);
}

function getPackFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('pack');

    return value ? String(value).trim() : '';
  } catch {
    return '';
  }
}

export default function BlueprintApp(props) {
  const {
    initialPackId = '',
    wpApiRoot = '',
    wpNonce = '',
    showDescription = true,
    showTime = true,
  } = props;

  const packFromUrl = useMemo(() => getPackFromUrl(), []);
  const effectiveInitialPackId = packFromUrl || asString(initialPackId).trim();

  const effectiveWpApiRoot = useMemo(() => asString(wpApiRoot).trim(), [wpApiRoot]);
  const effectiveWpNonce = useMemo(() => asString(wpNonce).trim(), [wpNonce]);

  const [isBlurred, setIsBlurred] = useState(false);

  const [pilotConsent, setPilotConsent] = useState({
    isLoading: true,
    isSaving: false,
    accepted: false,
    currentVersion: '',
    acceptedVersion: '',
    text: '',
    error: '',
  });

  useEffect(() => {
    let cancelled = false;

    async function loadConsent() {
      try {
        const response = await getPilotConsent();
        const data = response?.data ?? {};

        if (cancelled) {
          return;
        }

        setPilotConsent({
          isLoading: false,
          isSaving: false,
          accepted: Boolean(data.accepted),
          currentVersion: asString(data.current_version).trim(),
          acceptedVersion: asString(data.accepted_version).trim(),
          text: asString(data.text),
          error: '',
        });
      } catch {
        if (cancelled) {
          return;
        }

        setPilotConsent({
          isLoading: false,
          isSaving: false,
          accepted: false,
          currentVersion: '',
          acceptedVersion: '',
          text: '',
          error: 'Unable to load pilot consent.',
        });
      }
    }

    loadConsent();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function handleVisibilityChange() {
      setIsBlurred(document.visibilityState !== 'visible');

      if (document.visibilityState !== 'visible') {
        postSecurityEvent({
          event: 'tab_hidden',
          context: {
            reason: 'visibilitychange',
            packId: effectiveInitialPackId,
            path: window.location.pathname,
            href: window.location.href,
          },
        }).catch(() => {});
      }
    }

    function handleBlur() {
      setIsBlurred(true);

      postSecurityEvent({
        event: 'focus_lost',
        context: {
          reason: 'window_blur',
          packId: effectiveInitialPackId,
          path: window.location.pathname,
          href: window.location.href,
        },
      }).catch(() => {});
    }

    function handleFocus() {
      setIsBlurred(false);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [effectiveInitialPackId]);

  const pilotConsentReady = !pilotConsent.isLoading;
  const pilotConsentHasCurrentVersion = pilotConsent.currentVersion !== '';

  const pilotConsentVersionMismatch =
    pilotConsentHasCurrentVersion &&
    pilotConsent.acceptedVersion !== '' &&
    pilotConsent.acceptedVersion !== pilotConsent.currentVersion;

  const mustAcceptPilotConsent =
    pilotConsentReady &&
    pilotConsentHasCurrentVersion &&
    (!pilotConsent.accepted || pilotConsentVersionMismatch);

  const pilotConsentAccepted =
    pilotConsentReady && pilotConsentHasCurrentVersion && !mustAcceptPilotConsent;

  async function handlePilotConsentAccepted() {
    if (pilotConsent.isSaving || !pilotConsent.currentVersion) {
      return;
    }

    setPilotConsent((current) => ({
      ...current,
      isSaving: true,
      error: '',
    }));

    try {
      const response = await postPilotConsent({
        version: pilotConsent.currentVersion,
      });

      const data = response?.data ?? {};

      setPilotConsent((current) => ({
        ...current,
        isSaving: false,
        accepted: Boolean(data.accepted),
        currentVersion: asString(data.current_version).trim(),
        acceptedVersion: asString(data.accepted_version).trim(),
        error: '',
      }));
    } catch {
      setPilotConsent((current) => ({
        ...current,
        isSaving: false,
        accepted: false,
        error: 'Unable to save pilot consent. Please try again.',
      }));
    }
  }

  return (
    <PacksProvider wpApiRoot={effectiveWpApiRoot} wpNonce={effectiveWpNonce}>
      {mustAcceptPilotConsent ? (
        <PilotConsentModal
          policyText={pilotConsent.text}
          error={pilotConsent.error}
          isSaving={pilotConsent.isSaving}
          onAccepted={handlePilotConsentAccepted}
        />
      ) : null}

      <BlueprintProvider
        initialPackId={effectiveInitialPackId}
        wpApiRoot={effectiveWpApiRoot}
        wpNonce={effectiveWpNonce}
        showDescription={showDescription}
        showTime={showTime}
        pilotConsent={pilotConsent}
        pilotConsentAccepted={pilotConsentAccepted}
      >
        {isBlurred && (
          <div className="savora-blur-overlay">
            <div className="savora-blur-message">🔏 Screen hidden for security 🔏</div>
          </div>
        )}

        <BlueprintRouter />
      </BlueprintProvider>
    </PacksProvider>
  );
}
